/**
 * Date-range logic for the bulk "Accept in the past" flow.
 *
 * A backdated bulk-accept must pick a single date that is valid for *every*
 * portfolio in the batch. The batch's lower bound — the most recent
 * `max(proposalCreation, lastTradeDate)` across the selection — is computed by
 * the backend (`portfolioRepository.acceptLowerBound`); this module only turns it
 * into a picker range, whose upper bound is the server's yesterday.
 *
 * When the most recent proposal was created today (i.e. later than the server's
 * yesterday) there is no valid past date for the whole selection, so the range
 * is empty.
 *
 * Inputs are already-normalised local calendar dates (`YYYY-MM-DD`); ISO date
 * strings compare lexicographically in chronological order.
 */
export type BulkAcceptInThePastRange = {
	/** Earliest selectable day: the batch lower bound, or `undefined` if none is known. */
	minDate: string | undefined;
	/** Latest selectable day (the server's yesterday). */
	maxDate: string;
	/** `true` when no date satisfies every portfolio (the lower bound is newer than yesterday). */
	isEmpty: boolean;
};

export function computeBulkAcceptInThePastRange(
	latestProposalLocalDate: string | undefined | null,
	serverYesterdayLocalDate: string,
): BulkAcceptInThePastRange {
	const minDate = latestProposalLocalDate ?? undefined;
	return {
		minDate,
		maxDate: serverYesterdayLocalDate,
		isEmpty: minDate != null && minDate > serverYesterdayLocalDate,
	};
}
